define([
  'react',
  'prop-types',
  'immutable',
  'underscore',
  'es6!sumo/util',
  'es6!sumo/dom',
  'es6!launcher/util/listbuilder/constants',
  'es6!launcher/util/listbuilder/wysiwyg/util',
  'jsx!launcher/views/listbuilder/wysiwyg/components/Component',
], (React, PropTypes, Immutable, _, Util, DOM, Constants, WysiwygUtil, Component) => {
  const VALID_STYLE_ATTRS = [
    'color',
    'background',
    'backgroundColor',
    'backgroundPosition',
    'backgroundSize',
    'fill',
    'fontFamily',
    'fontWeight',
    'fontSize',
    'opacity',
  ];

  const { FormTypes } = Constants;
  const { classNames, fetchSupportedImageFormats } = Util;
  const { SVGImage } = DOM;

  // Save media size with the mediaId as the key.
  const imageSizeCache = {};

  @Component()
  class Image extends React.Component {
    static propTypes = {
      id: PropTypes.string.isRequired,
      mediaId: PropTypes.string,
      style: PropTypes.object,
      boxShadowStyle: PropTypes.object,
    };

    static defaultProps = {
      style: Immutable.Map(),
    }

    constructor(props) {
      super(props);
      const { mediaId } = props;
      const cachedSize = imageSizeCache[mediaId];

      // Zeros give us divide by zero issues.
      this.state = {
        width: 1,
        height: 1,
        defaultAspectRatio: 1,
        videoEl: {},
        supportedImageFormats: undefined,
        isSmartCropperAvailable: true,
      };

      if (cachedSize) this.state = cachedSize;

      this.onLoadedMetadata = this.onLoadedMetadata.bind(this);

      this.onLoadImage = this.onLoadImage.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    componentDidMount() {
      const { mediaType } = this.props;
      if (mediaType === 'video') {
        Util.addEvent(window, 'resize', this.onLoadedMetadata);
      }
    }

    componentWillReceiveProps(nextProps) {
      const { mediaId } = nextProps;
      const cachedSize = imageSizeCache[mediaId];

      // If the media size is cached, then we want to set the width and height.
      if (cachedSize) this.setState(cachedSize);
    }

    componentWillUnmount() {
      Util.removeEvent(window, 'resize', this.onLoadedMetadata);
    }

    componentWillMount() {
      fetchSupportedImageFormats()
        .then((supportedImageFormats) => {
          this.setState({ supportedImageFormats });

          const { api, mediaId, hash } = this.props;

          if (mediaId && mediaId.length) {
            const src = Util.getMediaUrl(api, mediaId);
            const convertedMediaURL = this.getConvertedMediaURL(src);

            // watch for a failed load so we can fallback
            if (convertedMediaURL) {
              const img = document.createElement('img');
              img.src = convertedMediaURL;
              img.onload = () => clearTimeout(this.timeout);
              img.onerror = this.fallback;

              // old method regularly comes in under 500ms, so we'll give the smart cropper a
              // little shorter leash for now so if we do fail, it won't be noticeable
              this.timeout = setTimeout(this.fallback.bind(null, 'timeout'), 400);
            }
          }
        });
    }

    onClick(event) {
      const { action, onClickAction } = this.props;
      if (action === 'nothing') return;
      if (onClickAction) onClickAction(action, this, event);
    }

    getConvertedMediaURL(src) {
      const { hash } = this.props;
      const { supportedImageFormats } = this.state;

      if (hash) {
        if (supportedImageFormats) {
          return src;
        }

        return undefined;
      }

      return src;
    }

    fallback = () => {
      const { isSmartCropperAvailable } = this.state;

      if (isSmartCropperAvailable) {
        this.setState({ isSmartCropperAvailable: false });
      }
    }

    onChangeImageSize(width, height) {
      const { mediaId } = this.props;
      if (!width || !height) return;

      imageSizeCache[mediaId] = { width, height };
      this.setState({ width, height });
    }

    onLoadImage(e) {
      const { mediaId, defaultMediaId, style, onSaveAspectRatio } = this.props;
      const img = e.target;
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      if (img) this.onChangeImageSize(width, height);
      if (mediaId === defaultMediaId) {
        onSaveAspectRatio(width / height);
      }
    }

    onLoadedMetadata(e) {
      if (e.target.nodeName === 'VIDEO') {
        this.setState({
          videoEl: e.target,
        });
      }
    }

    addFillModeStyle(src, imageStyle) {
      const {
        api,
        mediaId,
        mediaType,
        fill,
        style,
        type,
        formType,
      } = this.props;

      if (formType === FormTypes.SMART_BAR || formType === FormTypes.WELCOME_MAT) {
        const legacyBackgroundSize = imageStyle.backgroundSize;

        if (mediaType !== 'svg') {
          imageStyle.backgroundImage = `url(${src})`;
        }
        imageStyle.position = 'absolute';
        imageStyle.left = 0;
        imageStyle.top = 0;
        imageStyle.width = '100%';
        imageStyle.height = '100%';

        if (fill === 'tile' && mediaType !== 'video') {
          imageStyle.backgroundRepeat = 'repeat';
        } else {
          imageStyle.backgroundSize = '100% 100%';

          if (formType === FormTypes.WELCOME_MAT) {
            imageStyle.backgroundPosition = imageStyle.backgroundPosition || 'center';
            imageStyle.backgroundSize = legacyBackgroundSize || 'cover';
            imageStyle.backgroundRepeat = imageStyle.backgroundRepeat || 'no-repeat';
          }
        }
      } else {
        switch (fill) {
          case 'cover':
            const { width, height } = this.state;
            const position = WysiwygUtil.styleToPosition(style.toJS());
            const imageAspectRatio = width / height;
            const containerAspectRatio = position.width / position.height;

            // Center image in container.
            imageStyle.position = 'absolute';

            let computedWidth, computedHeight;
            let widthStyleAttr, heightStyleAttr;
            // Not sure why minHeight and minWidth work, but width and height don't
            // scale the image correctly, so we'll go with it.
            if (containerAspectRatio > imageAspectRatio) {
              computedWidth = Math.round(position.width);
              computedHeight = Math.round(1 / imageAspectRatio * position.width);
              widthStyleAttr = 'width';
              heightStyleAttr = 'minHeight';
            } else {
              computedWidth = Math.round(imageAspectRatio * position.height);
              computedHeight = Math.round(position.height);
              widthStyleAttr = 'minWidth';
              heightStyleAttr = 'height';
            }

            const marginLeft = Math.round(computedWidth / 2);
            const marginTop = Math.round(computedHeight / 2);

            imageStyle[heightStyleAttr] = `${computedHeight}px`;

            imageStyle.top = '50%';
            imageStyle.left = '50%';
            imageStyle.marginLeft = `-${marginLeft}px`;
            imageStyle.marginTop = `-${marginTop}px`;
            imageStyle[widthStyleAttr] = `${computedWidth}px`;
            break;
          // Default should be normal mode.
          default:
            imageStyle.width = '100%';
            imageStyle.height = '100%';
            break;
        }
      }
    }

    renderClickHitbox() {
      const { action, type } = this.props;
      if (action !== 'close' || type !== 'image' || !Util.isMobile()) return null;

      return (
        <div className="sumome-react-close-image-hitbox" onClick={this.onClick} />
      );
    }

    render() {
      const {
        api,
        fill,
        footer,
        mediaId,
        fallbackMediaId,
        mimetype,
        action,
        mediaType,
        boxShadowStyle,
        onMouseEnter,
        onMouseLeave,
        formType,
        popupWidth,
        popupHeight,
        isPreview,
        type,
        hash,
      } = this.props;
      const { supportedImageFormats, isSmartCropperAvailable } = this.state;
      const {
        videoHeight,
        videoWidth,
      } = this.state.videoEl || {};
      const style = _.pick(this.props.style.toJS(), VALID_STYLE_ATTRS);
      let imageComponent;

      style.padding = '0';
      style.margin = '0';
      style.display = 'block';

      if (mediaId && mediaId.length) {
        let src = Util.getMediaUrl(api, mediaId);

        // if we're gonna try the smart cropper, but we're not quite ready yet, hold off
        if (isSmartCropperAvailable && hash) {
          if (supportedImageFormats) {
            src = this.getConvertedMediaURL(src);
          } else {
            // lie in wait
            return <div />;
          }
        }

        this.addFillModeStyle(src, style);

        if (mediaType === 'svg') {
          let preserveAspectRatio = false;

          imageComponent = (
            <SVGImage
              src={src}
              style={style}
              preserveAspectRatio="none"
              onChangeSize={this.onChangeSvgSize}
            />
          )
        } else if (mediaType === 'video' && formType === FormTypes.WELCOME_MAT) {
          // Handle video component
          const videoRatio = videoWidth / videoHeight;
          const containerRatio = isPreview ? (popupWidth / popupHeight) : (window.innerWidth / window.innerHeight);

          imageComponent = (
            <div className="sumome-react-wysiwyg-video-wrapper" style={{ backgroundImage: fallbackMediaId ? `url(${Util.getMediaUrl(api, fallbackMediaId)})` : '' }}>
              <video
                className="sumome-react-wysiwyg-video"
                preload
                autoPlay
                loop
                muted
                playsInline
                onLoadedMetadata={this.onLoadedMetadata}
                poster="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgYAAAAAMAASsJTYQAAAAASUVORK5CYII="
              >
                <source src={src} type={mimetype} />
              </video>
            </div>
          );
        } else if (formType === FormTypes.SMART_BAR || formType === FormTypes.WELCOME_MAT) {
          imageComponent = (
            <div style={style}></div>
          );
        } else {
          // We only need to call addFillModeStyle() for images since svgs have built
          // in fit and cover fill modes.
          imageComponent = (
            <img
              src={src}
              style={style}
              onLoad={this.onLoadImage}
            />
          );
        }
      } else {
        style.width = '100%';
        style.height = '100%';

        // If there isn't a mediaId, the img tag renders a border around the
        // empty image.  I could do display:none, but we want background
        // colors so we substitute for a div.
        imageComponent = <div style={style} />;
      }

      // Z index makes the border radius work in chrome.
      const containerStyle = _.extend({
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        zIndex: 0,
        boxShadow: boxShadowStyle.boxShadow,
      }, _.pick(
        this.props.style.toJS(),
        'borderColor',
        'borderStyle',
        'borderTopLeftRadius',
        'borderTopRightRadius',
        'borderBottomLeftRadius',
        'borderBottomRightRadius',
        'borderTopWidth',
        'borderLeftWidth',
        'borderBottomWidth',
        'borderRightWidth',
      ));

      if (action !== 'nothing' && type === 'image') containerStyle.cursor = 'pointer';

      // The overlay renders the inset box shadow over the image.
      const overlayStyle = _.extend({
        position: 'absolute',
        top: '0',
        left: '0',
        bottom: '0',
        right: '0',
        boxShadow: boxShadowStyle.insetBoxShadow,
      });
      // For cover mode, we need to hide the image overflow. Also works for
      // normal mode since it doesn't overflow.
      return (
        <React.Fragment>
          <div
            style={containerStyle}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={this.onClick}
          >
            {imageComponent}
            <div style={overlayStyle} />
          </div>
          {this.renderClickHitbox()}
        </React.Fragment>
      );
    }
  }

  return Image;
});
